<script setup lang="ts">
import PdsButton from '@/components/pds/PdsButton.vue';
import PdsModal from '@/components/pds/PdsModal.vue';
import { computed, ref } from 'vue';
import { formatDate } from '../format';
import PickupCalendar from './PickupCalendar.vue';
import TimeStepper from './TimeStepper.vue';

export interface StockRange {
  from: string;
  to: string;
  hourFrom: string;
  hourTo: string;
}

const props = defineProps<{ initial: StockRange }>();
const emit = defineEmits<{
  close: [];
  apply: [range: StockRange];
  reset: [];
}>();

const DAY_MS = 86_400_000;
const CALENDAR_MONTHS = 12;

const isoDay = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
const fromIso = (value: string) => {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year, month - 1, day);
};
const shiftDays = (offset: number) => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() + offset);
  return isoDay(date);
};

// L'historique reste consultable : le calendrier remonte d'un an et monte d'un an.
const today = shiftDays(0);
const first = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() - CALENDAR_MONTHS);
  return isoDay(date);
})();
const last = (() => {
  const date = new Date();
  date.setMonth(date.getMonth() + CALENDAR_MONTHS);
  return isoDay(date);
})();

// Une période se choisit en deux touchers : le premier pose le début (déjà valable comme jour seul), le second le dernier jour.
const from = ref(props.initial.from);
const to = ref(props.initial.to || props.initial.from);
const awaitingEnd = ref(false);
const hourFrom = ref(props.initial.hourFrom);
const hourTo = ref(props.initial.hourTo);

const pick = (iso: string) => {
  if (!awaitingEnd.value) {
    from.value = to.value = iso;
    awaitingEnd.value = true;
    return;
  }
  awaitingEnd.value = false;
  if (iso < from.value) {
    to.value = from.value;
    from.value = iso;
  } else {
    to.value = iso;
  }
};

const PERIODS = [
  { label: 'Hier', from: shiftDays(-1), to: shiftDays(-1) },
  { label: 'Aujourd’hui', from: shiftDays(0), to: shiftDays(0) },
  { label: 'Demain', from: shiftDays(1), to: shiftDays(1) },
  { label: '7 prochains jours', from: shiftDays(0), to: shiftDays(6) },
  { label: '7 derniers jours', from: shiftDays(-6), to: shiftDays(0) },
];
const HOURS = [
  { label: 'Toute la journée', from: '', to: '' },
  { label: 'Matin', from: '', to: '12:00' },
  { label: 'Midi', from: '11:00', to: '14:00' },
  { label: 'Après-midi', from: '12:00', to: '18:00' },
  { label: 'Soir', from: '18:00', to: '' },
];

const usePeriod = (period: (typeof PERIODS)[number]) => {
  from.value = period.from;
  to.value = period.to;
  awaitingEnd.value = false;
};
const useHours = (hours: (typeof HOURS)[number]) => {
  hourFrom.value = hours.from;
  hourTo.value = hours.to;
};

const isPeriod = (period: (typeof PERIODS)[number]) =>
  from.value === period.from && to.value === period.to && !awaitingEnd.value;
const isHours = (hours: (typeof HOURS)[number]) =>
  hourFrom.value === hours.from && hourTo.value === hours.to;

const day = (iso: string) => formatDate(fromIso(iso).getTime());
const summary = computed(() => {
  if (!from.value) return 'Touchez un jour, puis un second pour une période.';
  if (awaitingEnd.value)
    return `${day(from.value)} · touchez le dernier jour, ou « Appliquer » pour ce seul jour`;
  if (from.value === to.value) return day(from.value);
  const count =
    Math.round(
      (fromIso(to.value).getTime() - fromIso(from.value).getTime()) / DAY_MS,
    ) + 1;
  return `${day(from.value)} → ${day(to.value)} · ${count} jours`;
});

// Une fin avant le début ne désigne rien : on le dit et on n'applique pas.
const hoursInverted = computed(
  () => !!hourFrom.value && !!hourTo.value && hourFrom.value > hourTo.value,
);

const apply = () => {
  if (hoursInverted.value) return;
  emit('apply', {
    from: from.value,
    to: to.value,
    hourFrom: hourFrom.value,
    hourTo: hourTo.value,
  });
};

const chip =
  'h-11 shrink-0 cursor-pointer rounded-full px-4 text-[14px] leading-5 font-semibold whitespace-nowrap';
</script>

<template>
  <PdsModal size="base" @close="emit('close')">
    <template #title>Période personnalisée</template>
    <template #subtitle>Une date, ou de telle date à telle date</template>

    <div class="flex flex-col gap-5 px-5 py-6">
      <div class="flex flex-col gap-2">
        <p class="text-encre-douce text-[12px] leading-[14px] font-semibold">
          Raccourcis
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="period in PERIODS"
            :key="period.label"
            type="button"
            :class="[
              chip,
              isPeriod(period) ? 'bg-encre text-carte' : 'bg-second text-encre',
            ]"
            :aria-pressed="isPeriod(period)"
            @click="usePeriod(period)"
          >
            {{ period.label }}
          </button>
        </div>
      </div>

      <div class="flex flex-col gap-2">
        <p
          class="text-[14px] leading-[1.4] font-semibold"
          role="status"
          aria-live="polite"
          data-testid="range-summary"
        >
          {{ summary }}
        </p>
        <PickupCalendar
          :selected="from"
          :to="to"
          :min="first"
          :max="last"
          :today="today"
          @pick="pick"
        />
      </div>

      <div class="flex flex-col gap-2">
        <p class="text-encre-douce text-[12px] leading-[14px] font-semibold">
          Retrait entre
        </p>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="hours in HOURS"
            :key="hours.label"
            type="button"
            :class="[
              chip,
              isHours(hours) ? 'bg-encre text-carte' : 'bg-second text-encre',
            ]"
            :aria-pressed="isHours(hours)"
            @click="useHours(hours)"
          >
            {{ hours.label }}
          </button>
        </div>
        <div class="flex gap-3">
          <TimeStepper v-model="hourFrom" label="De" start-at="08:00" />
          <TimeStepper v-model="hourTo" label="À" start-at="18:00" />
        </div>
        <p
          v-if="hoursInverted"
          role="alert"
          class="text-alerte-texte text-[12px] leading-[1.45]"
        >
          L'heure de fin doit venir après l'heure de début.
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex w-full items-center gap-3 sm:gap-4">
        <PdsButton variant="second" fit @click="emit('reset')">
          Réinitialiser
        </PdsButton>
        <PdsButton class="ml-auto" fit :disabled="hoursInverted" @click="apply">
          Appliquer
        </PdsButton>
      </div>
    </template>
  </PdsModal>
</template>
